import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * OAuth Popup Callback Page
 *
 * This page runs inside the popup window after OAuth redirect
 * Sends token to parent window via postMessage and closes
 */
const PopupCallback = () => {
	const location = useLocation();
	const [status, setStatus] = useState('processing');

	useEffect(() => {
		// Parse URL search params
		const searchParams = new URLSearchParams(location.search);
		const token = searchParams.get('token');
		const error = searchParams.get('error');

		if (error) {
			// Send error to parent window
			setStatus('error');

			if (window.opener) {
				window.opener.postMessage(
					{
						type: 'OAUTH_ERROR',
						error: decodeURIComponent(error),
					},
					window.location.origin, // Security: Only send to same origin
				);
			}

			// Close popup after 2 seconds
			setTimeout(() => {
				window.close();
			}, 2000);
		} else if (token) {
			// Send token to parent window
			setStatus('success');

			if (window.opener) {
				window.opener.postMessage(
					{
						type: 'OAUTH_SUCCESS',
						token: token,
					},
					window.location.origin, // Security: Only send to same origin
				);
			}

			// Close popup after 1 second
			setTimeout(() => {
				window.close();
			}, 1000);
		} else {
			// No token or error - something went wrong
			setStatus('error');

			if (window.opener) {
				window.opener.postMessage(
					{
						type: 'OAUTH_ERROR',
						error: 'Authentication failed - no token received',
					},
					window.location.origin,
				);
			}

			setTimeout(() => {
				window.close();
			}, 2000);
		}
	}, [location.search]);

	return (
		<div className="popup-callback">
			<div className="popup-callback-content">
				{status === 'processing' && (
					<>
						<div className="popup-spinner"></div>
						<p>Processing authentication...</p>
					</>
				)}

				{status === 'success' && (
					<>
						<div className="popup-success-icon">✓</div>
						<p>Authentication successful!</p>
						<p className="popup-subtext">Closing window...</p>
					</>
				)}

				{status === 'error' && (
					<>
						<div className="popup-error-icon">⚠️</div>
						<p>Authentication failed</p>
						<p className="popup-subtext">Closing window...</p>
					</>
				)}
			</div>

			<style jsx>{`
				.popup-callback {
					display: flex;
					align-items: center;
					justify-content: center;
					min-height: 100vh;
					background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
					font-family:
						-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
				}

				.popup-callback-content {
					text-align: center;
					color: white;
					padding: 40px;
				}

				.popup-spinner {
					width: 48px;
					height: 48px;
					border: 4px solid rgba(255, 255, 255, 0.3);
					border-top: 4px solid white;
					border-radius: 50%;
					margin: 0 auto 20px;
					animation: spin 1s linear infinite;
				}

				.popup-success-icon {
					width: 64px;
					height: 64px;
					background: rgba(76, 175, 80, 0.2);
					border: 3px solid #4caf50;
					border-radius: 50%;
					display: flex;
					align-items: center;
					justify-content: center;
					margin: 0 auto 20px;
					font-size: 32px;
					color: #4caf50;
					animation: scaleIn 0.3s ease;
				}

				.popup-error-icon {
					font-size: 48px;
					margin-bottom: 16px;
					animation: shake 0.5s ease;
				}

				.popup-callback-content p {
					margin: 8px 0;
					font-size: 18px;
					font-weight: 500;
				}

				.popup-subtext {
					font-size: 14px;
					opacity: 0.8;
					font-weight: 400;
				}

				@keyframes spin {
					0% {
						transform: rotate(0deg);
					}
					100% {
						transform: rotate(360deg);
					}
				}

				@keyframes scaleIn {
					0% {
						transform: scale(0);
					}
					50% {
						transform: scale(1.1);
					}
					100% {
						transform: scale(1);
					}
				}

				@keyframes shake {
					0%,
					100% {
						transform: translateX(0);
					}
					25% {
						transform: translateX(-10px);
					}
					75% {
						transform: translateX(10px);
					}
				}
			`}</style>
		</div>
	);
};

export default PopupCallback;
